import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AuditEntry } from "../lib/types";
import { useEffectiveUser } from "../lib/auth";
import { can, LEVEL_LABEL, isPlatformDeveloper } from "../lib/permissions";

const PAGE_SIZE = 500;

// Known entity types we've seen recorded in this app. Kept as a
// literal list so the filter dropdown doesn't surface every random
// string that sneaks into the audit table.
const ENTITY_TYPES = [
  "agency", "department", "employee", "shift", "shift_code",
  "assignment", "time_off", "user", "request_field",
] as const;

const ACTIONS: AuditEntry["action"][] = [
  "create", "update", "delete", "assign", "unassign", "login", "logout",
];

export function Audit() {
  const me = useEffectiveUser();

  // Date-range filter. Left blank = "most recent activity" (last
  // PAGE_SIZE rows). When either bound is set, the query switches
  // to a windowed fetch over that range so a supervisor can inquire
  // beyond the default 500-row window.
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // Cursor for "Load older" pagination. Holds the created_at of
  // the oldest currently-loaded row; the next page starts strictly
  // older than that. Reset whenever the date range changes so a new
  // window starts fresh.
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  // Rows accumulated from prior "Load older" clicks — appended to
  // the base page so the table grows without dropping what's above.
  const [olderPages, setOlderPages] = useState<AuditEntry[]>([]);

  const audit = useQuery({
    queryKey: ["audit", PAGE_SIZE, fromDate, toDate],
    queryFn: () => api.listAudit({
      limit: PAGE_SIZE,
      from: fromDate || undefined,
      to: toDate || undefined,
    }),
  });

  const loadOlderMut = useMutation({
    mutationFn: () => api.listAudit({
      limit: PAGE_SIZE,
      before: olderCursor ?? undefined,
      from: fromDate || undefined,
      to: toDate || undefined,
    }),
    onSuccess: (rows) => {
      if (rows.length === 0) {
        // Nothing older in this window — freeze the cursor so the
        // button reflects "no more rows".
        setOlderCursor(null);
        return;
      }
      setOlderPages((prev) => [...prev, ...rows]);
      setOlderCursor(rows[rows.length - 1].createdAt);
    },
  });

  // Reset paging when the base query changes (new date range or a
  // manual refetch). Without this, "Load older" would keep chasing
  // a stale cursor from the previous window.
  const baseRows = audit.data ?? [];
  const baseOldestAt = baseRows.length > 0 ? baseRows[baseRows.length - 1].createdAt : null;
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.listUsers() });
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const departments = useQuery({ queryKey: ["departments"], queryFn: () => api.listDepartments() });
  const shiftCodes = useQuery({ queryKey: ["shiftCodes"], queryFn: () => api.listShiftCodes() });

  const [entityFilter, setEntityFilter] = useState<string>("");
  const [actionFilter, setActionFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Combined visible rows (base page + any older pages loaded). We
  // keep them in memo separately from the filter pass so the count
  // in the toolbar can show the true rows-loaded number.
  const allRows = useMemo(() => {
    if (olderPages.length === 0) return baseRows;
    return [...baseRows, ...olderPages];
  }, [baseRows, olderPages]);

  // Whenever the base window's oldest row shifts (new range,
  // refetch), reset the cursor + accumulated pages so "Load older"
  // starts from the new floor and doesn't chase a stale cursor.
  useEffect(() => {
    setOlderCursor(baseOldestAt);
    setOlderPages([]);
  }, [fromDate, toDate, baseOldestAt]);

  // userId → display label. The platform developer (the one account
  // anywhere that's the operator of ShiftManager itself, see
  // isPlatformDeveloper in lib/permissions) renders as "Developer";
  // every other account — including ordinary agency Owners — uses
  // their linked employee's real name when available, falling back
  // to "username (Level)" for bare accounts and a short UID for
  // orphaned audit rows.
  const userLabel = useMemo(() => {
    const byId = new Map<string, string>();
    const empById = new Map((employees.data ?? []).map((e) => [e.id, e]));
    for (const u of users.data ?? []) {
      if (isPlatformDeveloper(u)) {
        byId.set(u.id, "Developer");
        continue;
      }
      const emp = u.employeeId ? empById.get(u.employeeId) : undefined;
      if (emp) byId.set(u.id, `${emp.firstName} ${emp.lastName}`.trim() || u.username);
      else byId.set(u.id, `${u.username} (${LEVEL_LABEL[u.level]})`);
    }
    return byId;
  }, [users.data, employees.data]);

  // Lookup maps for entity resolution. Each maps id → display string
  // for the rows the audit log most commonly references.
  const empById = useMemo(
    () => new Map((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );
  const shiftById = useMemo(
    () => new Map((shifts.data ?? []).map((s) => [s.id, s])),
    [shifts.data],
  );
  const deptById = useMemo(
    () => new Map((departments.data ?? []).map((d) => [d.id, d])),
    [departments.data],
  );
  const usersById = useMemo(
    () => new Map((users.data ?? []).map((u) => [u.id, u])),
    [users.data],
  );
  const codeById = useMemo(
    () => new Map((shiftCodes.data ?? []).map((c) => [c.id, c])),
    [shiftCodes.data],
  );

  function shortId(id: string): string {
    return `${id.slice(0, 8)}…`;
  }

  // Resolve an audit entry's target row to a human label.
  function entityLabel(a: AuditEntry): string {
    // Prefer the "after" snapshot (creates / updates) so deletes
    // still carry the deleted row's details from the "before" side.
    const row = (a.afterJson ?? a.beforeJson) as Record<string, unknown> | null;
    const fromRow = (k: string | string[]): string | undefined => {
      if (!row) return undefined;
      const keys = Array.isArray(k) ? k : [k];
      for (const key of keys) {
        const v = (row as Record<string, unknown>)[key];
        if (typeof v === "string" && v.length > 0) return v;
      }
      return undefined;
    };
    switch (a.entityType) {
      case "employee": {
        const e = empById.get(a.entityId);
        if (e) return `${e.lastName}, ${e.firstName}`.trim() || shortId(a.entityId);
        const f = fromRow(["firstName", "first_name"]);
        const l = fromRow(["lastName", "last_name"]);
        if (f || l) return `${l ?? ""}, ${f ?? ""}`.trim().replace(/^,\s*/, "");
        return shortId(a.entityId);
      }
      case "shift": {
        return shiftById.get(a.entityId)?.name ?? fromRow("name") ?? shortId(a.entityId);
      }
      case "department": {
        return deptById.get(a.entityId)?.name ?? fromRow("name") ?? shortId(a.entityId);
      }
      case "shift_code": {
        const sc = codeById.get(a.entityId);
        return sc ? `${sc.code} — ${sc.label}` : (fromRow("code") ?? shortId(a.entityId));
      }
      case "user": {
        const u = usersById.get(a.entityId);
        return u?.username ?? fromRow("username") ?? shortId(a.entityId);
      }
      case "assignment": {
        const empId = fromRow(["employeeId", "employee_id"]);
        const date = fromRow("date");
        const shiftId = fromRow(["shiftId", "shift_id"]);
        const emp = empId ? empById.get(empId) : undefined;
        const sh = shiftId ? shiftById.get(shiftId) : undefined;
        const parts = [
          emp ? `${emp.lastName}, ${emp.firstName}` : undefined,
          date,
          sh?.name,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(" · ") : shortId(a.entityId);
      }
      case "time_off": {
        const empId = fromRow(["employeeId", "employee_id"]);
        const type = fromRow("type");
        const start = fromRow(["startDate", "start_date"]);
        const end = fromRow(["endDate", "end_date"]);
        const emp = empId ? empById.get(empId) : undefined;
        const parts = [
          emp ? `${emp.lastName}, ${emp.firstName}` : undefined,
          type,
          start && end ? `${start} → ${end}` : undefined,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(" · ") : shortId(a.entityId);
      }
      case "agency":
        return fromRow("name") ?? "Agency";
      case "request_field":
        return fromRow("label") ?? fromRow("key") ?? shortId(a.entityId);
      case "settings": {
        // entityId carries the setting key for our setters
        // (brand_image, seniority_mode, pay_weekend_weeks). Drop the
        // underscores so the audit row reads as a sentence.
        const k = (a.entityId || "").replace(/_/g, " ");
        return k || "agency setting";
      }
      case "shift_assignment": {
        const empId = fromRow(["employeeId", "employee_id"]);
        const shiftId = fromRow(["shiftId", "shift_id"]);
        const start = fromRow(["startDate", "start_date"]);
        const end = fromRow(["endDate", "end_date"]);
        const emp = empId ? empById.get(empId) : undefined;
        const sh = shiftId ? shiftById.get(shiftId) : undefined;
        const range = start && end ? `${start} → ${end}` : (start ?? end);
        const parts = [
          emp ? `${emp.lastName}, ${emp.firstName}` : undefined,
          sh?.name,
          range,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(" · ") : shortId(a.entityId);
      }
      default:
        return shortId(a.entityId);
    }
  }

  // Filter + search in one pass so the derived list stays cheap.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRows.filter((a) => {
      if (entityFilter && a.entityType !== entityFilter) return false;
      if (actionFilter && a.action !== actionFilter) return false;
      if (!term) return true;
      const bag = [
        a.action,
        a.entityType,
        a.entityId,
        entityLabel(a),
        a.userId ? (userLabel.get(a.userId) ?? a.userId) : "",
        a.beforeJson ? JSON.stringify(a.beforeJson) : "",
        a.afterJson ? JSON.stringify(a.afterJson) : "",
      ].join(" ").toLowerCase();
      return bag.includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, entityFilter, actionFilter, search, userLabel,
      empById, shiftById, deptById, usersById, codeById]);

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  if (!can.viewAudit(me)) {
    return (
      <div className="p-4 md:p-6">
        <div className="p-6 bg-slate-50 border border-slate-200 rounded text-center">
          <h1 className="text-lg font-semibold text-slate-900">Audit log</h1>
          <p className="text-sm text-slate-500 mt-2">
            Your account level does not have access to the audit log.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-slate-900">Audit log</h1>
        <p className="text-sm text-slate-500">
          Append-only. Click a row to see the before / after snapshot.
          Nothing is ever aged out — use From / To to inquire on
          older activity or Load older to keep pulling in {PAGE_SIZE}-row pages.
        </p>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg mb-3 p-3 flex flex-wrap items-center gap-3">
        <label className="text-xs text-slate-500 flex items-center gap-1.5">
          Entity
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="text-sm px-2 py-1 rounded border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
          >
            <option value="">All</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-500 flex items-center gap-1.5">
          Action
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="text-sm px-2 py-1 rounded border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
          >
            <option value="">All</option>
            {ACTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-500 flex items-center gap-1.5">
          From
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="text-sm px-2 py-1 rounded border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
          />
        </label>
        <label className="text-xs text-slate-500 flex items-center gap-1.5">
          To
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="text-sm px-2 py-1 rounded border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
          />
        </label>
        {(fromDate || toDate) && (
          <button
            type="button"
            onClick={() => { setFromDate(""); setToDate(""); }}
            className="text-xs text-sky-700 hover:underline"
          >
            Clear dates
          </button>
        )}
        <div className="flex-1 min-w-[200px]">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, id, action, or payload…"
            className="w-full text-sm px-3 py-1 rounded border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
          />
        </div>
        <span className="text-xs text-slate-400">
          {visible.length} of {allRows.length}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium w-8"></th>
              <th className="px-3 py-2 font-medium">Time</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Entity</th>
              <th className="px-3 py-2 font-medium">Target</th>
              <th className="px-3 py-2 font-medium">By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map((a) => {
              const isOpen = expanded.has(a.id);
              const hasDiff = a.beforeJson !== null || a.afterJson !== null;
              return (
                <AuditRow
                  key={a.id}
                  entry={a}
                  isOpen={isOpen}
                  hasDiff={hasDiff}
                  label={entityLabel(a)}
                  byLabel={a.userId
                    ? (userLabel.get(a.userId) ?? shortId(a.userId))
                    : "—"}
                  onToggle={() => toggleExpanded(a.id)}
                />
              );
            })}
          </tbody>
        </table>
        {visible.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">
            {allRows.length === 0
              ? "No activity yet."
              : "No entries match the current filters."}
          </div>
        )}
      </div>

      {/* "Load older" only makes sense once the base window has
        * returned a full page — if the initial fetch was under the
        * page size, everything within the current filter is already
        * on screen and there's nothing older to pull. Once the last
        * click returns zero rows, the button switches to a static
        * "no older" line so a supervisor knows they've hit the floor. */}
      {allRows.length >= PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-center">
          {olderCursor === null && olderPages.length > 0 ? (
            <span className="text-xs text-slate-400">
              No older activity in this window.
            </span>
          ) : (
            <button
              type="button"
              onClick={() => loadOlderMut.mutate()}
              disabled={loadOlderMut.isPending || olderCursor === null}
              className="text-sm px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadOlderMut.isPending
                ? "Loading…"
                : `Load older ${PAGE_SIZE} rows`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const ACTION_COLOR: Record<string, string> = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-sky-100 text-sky-800",
  delete: "bg-red-100 text-red-800",
  assign: "bg-indigo-100 text-indigo-800",
  unassign: "bg-amber-100 text-amber-800",
  login: "bg-slate-100 text-slate-700",
  logout: "bg-slate-100 text-slate-500",
};

function AuditRow({
  entry, isOpen, hasDiff, label, byLabel, onToggle,
}: {
  entry: AuditEntry;
  isOpen: boolean;
  hasDiff: boolean;
  label: string;
  byLabel: string;
  onToggle: () => void;
}) {
  const colour = ACTION_COLOR[entry.action] ?? "bg-slate-100 text-slate-700";
  return (
    <>
      <tr
        className={"hover:bg-slate-50 " + (hasDiff ? "cursor-pointer" : "")}
        onClick={hasDiff ? onToggle : undefined}
      >
        <td className="px-3 py-2 text-slate-400 text-xs w-8">
          {hasDiff ? (isOpen ? "▾" : "▸") : ""}
        </td>
        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
          {new Date(entry.createdAt).toLocaleString()}
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${colour}`}>
            {entry.action}
          </span>
        </td>
        <td className="px-3 py-2 capitalize text-slate-700 whitespace-nowrap">
          {entry.entityType.replace(/_/g, " ")}
        </td>
        <td className="px-3 py-2 text-slate-900">{label}</td>
        <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{byLabel}</td>
      </tr>
      {isOpen && hasDiff && (
        <tr className="bg-slate-50">
          <td />
          <td colSpan={5} className="px-3 py-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <DiffPane title="Before" value={entry.beforeJson} />
              <DiffPane title="After" value={entry.afterJson} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DiffPane({ title, value }: { title: string; value: unknown }) {
  const text = value === null || value === undefined
    ? "—"
    : JSON.stringify(value, null, 2);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {title}
      </div>
      <pre className="bg-white border border-slate-200 rounded p-2 overflow-auto max-h-72 text-[11px] text-slate-800 font-mono whitespace-pre-wrap break-all">
        {text}
      </pre>
    </div>
  );
}
